import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';

// Fetch Appointments
export const fetchAppointments = createAsyncThunk(
  'appointments/fetch',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { page = 1, page_size, search, status, doctor, patient, clinic, scheduled_date, ordering } = params;
      const queryParams = new URLSearchParams();
      
      if (page) queryParams.append('page', page);
      if (page_size) queryParams.append('page_size', page_size);
      if (search) queryParams.append('search', search);
      if (status && status !== 'All') queryParams.append('status', status);
      if (doctor) queryParams.append('doctor', doctor);
      if (patient) queryParams.append('patient', patient);
      if (clinic) queryParams.append('clinic', clinic);
      if (scheduled_date) queryParams.append('scheduled_date', scheduled_date);
      if (ordering) queryParams.append('ordering', ordering);
      
      // Default page_size if not provided
      if (!queryParams.has('page_size')) {
        queryParams.append('page_size', '100'); // Default to 100 results per page
      }
      
      const queryString = queryParams.toString();
      const url = `/appointments/${queryString ? `?${queryString}` : ''}`;
      
      console.log('📅 Fetching Appointments:', { url, params: Object.fromEntries(queryParams) });
      
      const response = await axiosInstance.get(url);
      
      console.log('📅 Fetch Appointments Response:', {
        url,
        status: response.status,
        dataKeys: Object.keys(response.data || {}),
        resultsCount: response.data?.results?.length || (Array.isArray(response.data) ? response.data.length : 0),
        totalCount: response.data?.count,
        hasNext: !!response.data?.next,
        hasPrevious: !!response.data?.previous
        // Removed fullResponse to avoid logging large objects
      });
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || error.response?.data || 'Failed to fetch appointments'
      );
    }
  }
);

// Fetch Appointment Stats
export const fetchAppointmentStats = createAsyncThunk(
  'appointments/fetchStats',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { start_date, end_date, clinic, doctor, search, status } = params;
      const queryParams = new URLSearchParams();
      
      // NO FILTERS - stats are overall totals only
      // (Keeping params structure for backward compatibility but not using them)
      
      const queryString = queryParams.toString();
      const url = `/appointments/stats/${queryString ? `?${queryString}` : ''}`;
      
      console.log('📊 Fetching appointment stats:', { url, params, fullUrl: `${axiosInstance.defaults.baseURL}${url}` });
      
      const response = await axiosInstance.get(url);
      
      try {
        console.log('📊 Stats response from backend:', JSON.stringify(response.data, null, 2));
      } catch (e) {
        console.log('📊 Stats response from backend (non-serializable):', response.data);
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching appointment stats:', error);
      return rejectWithValue(
        error.response?.data?.detail || error.response?.data?.error || error.response?.data || 'Failed to fetch appointment stats'
      );
    }
  }
);

// Confirm Appointment
export const confirmAppointment = createAsyncThunk(
  'appointments/confirm',
  async (appointmentId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/appointments/${appointmentId}/confirm/`);
      // Response includes full appointment data
      return response.data?.appointment || { id: appointmentId, status: 'Confirmed' };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || error.response?.data?.error || error.response?.data || 'Failed to confirm appointment'
      );
    }
  }
);

// Reschedule Appointment
export const rescheduleAppointment = createAsyncThunk(
  'appointments/reschedule',
  async ({ id, new_date, new_time, reason }, { rejectWithValue }) => {
    try {
      // Ensure time is in 24-hour format (HH:MM)
      let formattedTime = new_time;
      if (new_time && (new_time.includes('AM') || new_time.includes('PM') || new_time.includes('am') || new_time.includes('pm'))) {
        // Convert 12-hour to 24-hour format
        const [time, period] = new_time.split(/\s*(AM|PM|am|pm)/);
        const [hours, minutes] = time.split(':');
        let hour24 = parseInt(hours, 10);
        if (period && period.toUpperCase() === 'PM' && hour24 !== 12) {
          hour24 += 12;
        } else if (period && period.toUpperCase() === 'AM' && hour24 === 12) {
          hour24 = 0;
        }
        formattedTime = `${hour24.toString().padStart(2, '0')}:${minutes || '00'}`;
      }
      
      const response = await axiosInstance.put(`/appointments/${id}/reschedule/`, {
        new_date,
        new_time: formattedTime,
        reason,
      });
      
      // Response should include updated appointment data
      if (response.data?.appointment) {
        return response.data.appointment;
      }
      
      return {
        id,
        scheduled_date: new_date,
        scheduled_time: formattedTime,
        status: 'Confirmed', // Reschedule sets to Confirmed
        reschedule_reason: reason,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || error.response?.data?.error || error.response?.data || 'Failed to reschedule appointment'
      );
    }
  }
);

// Cancel Appointment
export const cancelAppointment = createAsyncThunk(
  'appointments/cancel',
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/appointments/${id}/cancel/`, { reason });
      
      // Response should include updated appointment or confirmation
      return response.data?.appointment || { id, status: 'Canceled', cancel_reason: reason };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || error.response?.data?.error || error.response?.data || 'Failed to cancel appointment'
      );
    }
  }
);

// Update Appointment Status
export const updateAppointmentStatus = createAsyncThunk(
  'appointments/updateStatus',
  async ({ id, status, notes }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`/appointments/${id}/`, {
        status,
        notes
      });
      return { id, status, notes, updated_at: response.data.updated_at };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to update appointment status'
      );
    }
  }
);

// Check Availability
export const checkAvailability = createAsyncThunk(
  'appointments/checkAvailability',
  async ({ doctorId, date, time, duration }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/appointments/check-availability/', {
        doctor_id: doctorId,
        scheduled_date: date,
        scheduled_time: time,
        duration
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to check availability'
      );
    }
  }
);

// Get Reminder Settings
export const getReminderSettings = createAsyncThunk(
  'appointments/getReminderSettings',
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/appointments/reminder-settings/${userId}/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to get reminder settings'
      );
    }
  }
);

// Update Reminder Settings
export const updateReminderSettings = createAsyncThunk(
  'appointments/updateReminderSettings',
  async ({ userId, settings }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/appointments/reminder-settings/${userId}/`, settings);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to update reminder settings'
      );
    }
  }
);

// Send Test Reminder
export const sendTestReminder = createAsyncThunk(
  'appointments/sendTestReminder',
  async ({ userId, type }, { rejectWithValue }) => {
    try {
      await axiosInstance.post('/appointments/send-test-reminder/', {
        user_id: userId,
        reminder_type: type
      });
      return { success: true };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to send test reminder'
      );
    }
  }
);

const appointmentSlice = createSlice({
  name: 'appointments',
  initialState: {
    data: [],
    results: [],
    count: 0,
    next: null,
    previous: null,
    stats: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateAppointmentInList: (state, action) => {
      const { id, updates } = action.payload;
      const index = state.data.findIndex(appt => appt.id === id);
      if (index !== -1) {
        state.data[index] = { ...state.data[index], ...updates };
        if (state.results) {
          const resultIndex = state.results.findIndex(appt => appt.id === id);
          if (resultIndex !== -1) {
            state.results[resultIndex] = { ...state.results[resultIndex], ...updates };
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Helper function to normalize appointment objects (ensure doctor/patient/clinic are IDs, not objects)
    const normalizeAppointment = (appt) => {
      if (!appt) return appt;
      const normalized = { ...appt };
      // Ensure doctor is an ID
      if (normalized.doctor && typeof normalized.doctor === 'object') {
        normalized.doctor = normalized.doctor?.id || normalized.doctor_id || normalized.doctor;
      }
      // Ensure patient is an ID
      if (normalized.patient && typeof normalized.patient === 'object') {
        normalized.patient = normalized.patient?.id || normalized.patient_id || normalized.patient;
      }
      // Ensure clinic is an ID or null
      if (normalized.clinic && typeof normalized.clinic === 'object') {
        normalized.clinic = normalized.clinic?.id || normalized.clinic_id || normalized.clinic;
      } else if (!normalized.clinic) {
        normalized.clinic = normalized.clinic_id || null;
      }
      return normalized;
    };

    builder
      // Fetch Appointments
      .addCase(fetchAppointments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppointments.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        
        // Handle different response structures
        const payload = action.payload;
        const results = payload?.results || payload?.data || (Array.isArray(payload) ? payload : []);
        const count = payload?.count !== undefined ? payload.count : (Array.isArray(results) ? results.length : 0);
        
        const normalizedResults = Array.isArray(results) ? results.map(normalizeAppointment) : [];
        
        console.log('✅ Fetch Appointments Fulfilled:', {
          payloadKeys: Object.keys(payload || {}),
          resultsLength: normalizedResults.length,
          count,
          hasResults: !!payload?.results,
          hasData: !!payload?.data,
          isArray: Array.isArray(payload)
        });
        
        state.data = normalizedResults;
        state.results = normalizedResults;
        state.count = count;
        state.next = payload?.next || null;
        state.previous = payload?.previous || null;
      })
      .addCase(fetchAppointments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Stats
      .addCase(fetchAppointmentStats.pending, (state) => {
        // Don't set loading to true as stats loading doesn't block UI
      })
      .addCase(fetchAppointmentStats.fulfilled, (state, action) => {
        try {
          console.log('📊 Stats stored in Redux:', JSON.stringify(action.payload, null, 2));
        } catch (e) {
          console.log('📊 Stats stored in Redux (non-serializable):', action.payload);
        }
        state.stats = action.payload;
      })
      .addCase(fetchAppointmentStats.rejected, (state, action) => {
        // Don't set error as stats failure shouldn't block main functionality
        console.error('❌ Failed to fetch appointment stats:', action.payload);
      })

      // Confirm Appointment
      .addCase(confirmAppointment.pending, (state) => {
        // Don't set loading to true for individual actions
      })
      .addCase(confirmAppointment.fulfilled, (state, action) => {
        const updatedAppointment = normalizeAppointment(action.payload);
        const index = state.data.findIndex((appt) => appt.id === updatedAppointment.id);
        if (index !== -1) {
          state.data[index] = { ...state.data[index], ...updatedAppointment };
        }
        // Also update in results if exists
        if (state.results) {
          const resultIndex = state.results.findIndex((appt) => appt.id === updatedAppointment.id);
          if (resultIndex !== -1) {
            state.results[resultIndex] = { ...state.results[resultIndex], ...updatedAppointment };
          }
        }
      })
      .addCase(confirmAppointment.rejected, (state, action) => {
        // Error is handled in component, don't set global error for individual actions
        console.error('Confirm appointment failed:', action.payload);
      })

      // Reschedule Appointment
      .addCase(rescheduleAppointment.pending, (state) => {
        // Don't set loading to true for individual actions
      })
      .addCase(rescheduleAppointment.fulfilled, (state, action) => {
        const updatedAppointment = normalizeAppointment(action.payload);
        const index = state.data.findIndex((appt) => appt.id === updatedAppointment.id);
        if (index !== -1) {
          state.data[index] = { ...state.data[index], ...updatedAppointment };
        }
        // Also update in results if exists
        if (state.results) {
          const resultIndex = state.results.findIndex((appt) => appt.id === updatedAppointment.id);
          if (resultIndex !== -1) {
            state.results[resultIndex] = { ...state.results[resultIndex], ...updatedAppointment };
          }
        }
      })
      .addCase(rescheduleAppointment.rejected, (state, action) => {
        // Error is handled in component
        console.error('Reschedule appointment failed:', action.payload);
      })

      // Cancel Appointment
      .addCase(cancelAppointment.pending, (state) => {
        // Don't set loading to true for individual actions
      })
      .addCase(cancelAppointment.fulfilled, (state, action) => {
        const updatedAppointment = normalizeAppointment(action.payload);
        const index = state.data.findIndex((appt) => appt.id === updatedAppointment.id);
        if (index !== -1) {
          state.data[index] = { ...state.data[index], ...updatedAppointment };
        }
        // Also update in results if exists
        if (state.results) {
          const resultIndex = state.results.findIndex((appt) => appt.id === updatedAppointment.id);
          if (resultIndex !== -1) {
            state.results[resultIndex] = { ...state.results[resultIndex], ...updatedAppointment };
          }
        }
      })
      .addCase(cancelAppointment.rejected, (state, action) => {
        // Error is handled in component
        console.error('Cancel appointment failed:', action.payload);
      })

      // Update Appointment Status
      .addCase(updateAppointmentStatus.pending, (state) => {
        // Don't set loading to true for individual actions
      })
      .addCase(updateAppointmentStatus.fulfilled, (state, action) => {
        const updatedAppointment = normalizeAppointment(action.payload);
        const index = state.data.findIndex((appt) => appt.id === updatedAppointment.id);
        if (index !== -1) {
          state.data[index] = { ...state.data[index], ...updatedAppointment };
        }
        // Also update in results if exists
        if (state.results) {
          const resultIndex = state.results.findIndex((appt) => appt.id === updatedAppointment.id);
          if (resultIndex !== -1) {
            state.results[resultIndex] = { ...state.results[resultIndex], ...updatedAppointment };
          }
        }
      })
      .addCase(updateAppointmentStatus.rejected, (state, action) => {
        // Error is handled in component
        console.error('Update appointment status failed:', action.payload);
      })

      // Check Availability
      .addCase(checkAvailability.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkAvailability.fulfilled, (state, action) => {
        state.loading = false;
        // Availability check doesn't update the appointments list
      })
      .addCase(checkAvailability.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Reminder Settings
      .addCase(getReminderSettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(getReminderSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.reminderSettings = action.payload;
      })
      .addCase(getReminderSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Reminder Settings
      .addCase(updateReminderSettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateReminderSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.reminderSettings = action.payload;
      })
      .addCase(updateReminderSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Send Test Reminder
      .addCase(sendTestReminder.pending, (state) => {
        state.loading = true;
      })
      .addCase(sendTestReminder.fulfilled, (state, action) => {
        state.loading = false;
        // Test reminder doesn't update the appointments list
      })
      .addCase(sendTestReminder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, updateAppointmentInList } = appointmentSlice.actions;
export default appointmentSlice.reducer;
